import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import Picker from '../../../common/components/picker/picker';
import { searchProjects } from '../../projects/contexts/projects';
import { useCreatePlan } from '../contexts/plans';
import {
    EMPTY_PLAN, NAME_MAX, PLAN_TEMPLATE, SCOPE_ALL, SCOPE_OPTIONS, SCOPE_SOME,
    toCreatePlanPayload, validatePlan,
} from '../utils/form';
import MarkdownEditor from './markdown-editor';

import '../styles/plans.css';
import '../../clients/styles/form.css';

// Creating a plan, which creates its first draft with it.
//
// NOBODY TYPES AN IDENTIFIER HERE. The project arrives from the workspace the
// person came from (`?project=N`), and the plan's document is created by the
// server — the panel never sees or sends a `document_id` or a
// `document_version_id`.

export default function CreatePlanForm() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const createPlan = useCreatePlan();

    const [values, setValues] = useState(() => ({
        ...EMPTY_PLAN,
        project_id: params.get('project') || '',
    }));
    const [errors, setErrors] = useState({});

    const change = (field, value) => {
        setValues(prev => ({ ...prev, [field]: value }));
        setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const found = validatePlan(values, { withProject: true });
        if (Object.keys(found).length) {
            setErrors(found);
            return;
        }

        try {
            const created = await createPlan.mutateAsync(toCreatePlanPayload(values));
            const plan = created?.plan ?? created;
            toast.success('Plan creado, con su primer borrador');
            navigate(`/admin/plans/${plan.id}`, { replace: true });
        } catch (err) {
            if (err.fields) setErrors(err.fields);
            else toast.error(err.message || 'No se pudo crear el plan');
        }
    };

    return (
        <section className="client-form">
            <header className="client-form__header">
                <h2 className="client-form__title">Nuevo plan</h2>
                <p className="client-form__subtitle">
                    Un plan es de dónde salen los tiers. Se crea con su primer borrador; se
                    presenta, se revisa y se aprueba después.
                </p>
            </header>

            <form onSubmit={handleSubmit} noValidate>
                <div className="client-form__card">
                    <div className="client-form__grid">
                        <label className="client-form__field">
                            <span className="client-form__label">
                                Proyecto <span className="client-form__required">*</span>
                            </span>
                            <span className="client-form__control">
                                <Picker
                                    value={values.project_id}
                                    onChange={(id) => change('project_id', id)}
                                    fetchOptions={searchProjects}
                                    cacheKey="projects"
                                    labelOf={project => project.name}
                                    subtitleOf={project => project.slug}
                                    placeholder="Elegir proyecto..."
                                    disabled={createPlan.isPending}
                                    invalid={!!errors.project_id}
                                />
                            </span>
                            {errors.project_id && <span className="client-form__error">{errors.project_id}</span>}
                        </label>

                        <label className="client-form__field">
                            <span className="client-form__label">
                                Nombre <span className="client-form__required">*</span>
                            </span>
                            <span className="client-form__control">
                                <input
                                    className={`client-form__input ${errors.name ? 'client-form__input--invalid' : ''}`}
                                    type="text"
                                    value={values.name}
                                    onChange={e => change('name', e.target.value)}
                                    maxLength={NAME_MAX}
                                    placeholder="Planeación de..."
                                    disabled={createPlan.isPending}
                                    required
                                />
                            </span>
                            {errors.name && <span className="client-form__error">{errors.name}</span>}
                        </label>
                    </div>

                    <fieldset className="plans__scope" disabled={createPlan.isPending}>
                        <legend className="plans__scope-legend">
                            Alcance <span className="client-form__required">*</span>
                        </legend>
                        {SCOPE_OPTIONS.map(option => (
                            <label key={option.value} className="plans__scope-option">
                                <input
                                    type="radio"
                                    name="scope"
                                    value={option.value}
                                    checked={values.scope === option.value}
                                    onChange={() => change('scope', option.value)}
                                />
                                <span className="plans__scope-text">
                                    <span className="plans__scope-label">{option.label}</span>
                                    <span className="plans__scope-hint">{option.hint}</span>
                                </span>
                            </label>
                        ))}
                        {/* Asked as a question instead of inferred from an empty
                            box: in this contract an empty list is the WIDEST
                            scope, so leaving it blank by accident would quietly
                            grant the whole project. */}
                        {values.scope === SCOPE_ALL && (
                            <p className="plans__note">
                                Se guarda como lista vacía, que acá significa <strong>todo el
                                proyecto</strong> — no «sin repositorios».
                            </p>
                        )}
                        {values.scope === SCOPE_SOME && (
                            <p className="plans__note">
                                Los repositorios se eligen al editar la versión, una vez creada.
                            </p>
                        )}
                        {errors.repository_ids && (
                            <span className="client-form__error">{errors.repository_ids}</span>
                        )}
                    </fieldset>

                    <div>
                        <MarkdownEditor
                            value={values.content}
                            onChange={content => change('content', content)}
                            rows={20}
                            disabled={createPlan.isPending}
                        >
                            {!values.content.trim() && (
                                <button
                                    type="button"
                                    className="plans__template"
                                    onClick={() => change('content', PLAN_TEMPLATE)}
                                >
                                    Usar la plantilla
                                </button>
                            )}
                        </MarkdownEditor>
                        {errors.content && <span className="client-form__error">{errors.content}</span>}
                    </div>
                </div>

                <div className="client-form__actions">
                    <Link to="/admin/plans" className="client-form__cancel">Cancelar</Link>
                    <button type="submit" className="client-form__submit" disabled={createPlan.isPending}>
                        {createPlan.isPending ? 'Creando...' : 'Crear plan'}
                    </button>
                </div>
            </form>
        </section>
    );
}
